import type jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import {
  PDFQuoteData,
  buildPalette,
  loadLogo,
  getJsPdfImageFormat,
  resolveDocumentTitle,
  renderDocumentSubtitle,
  fitHeaderTitle,
  formatQuoteDate,
  calcValidityDate,
  buildItemRows,
  formatCurrency,
  renderDiagnosisSection,
  renderServiceOrderSections,
  renderPaymentSection,
  renderExtraFields,
  renderPixSection,
  renderPhotosSection,
  renderSignatureSection,
  renderFooter,
  clientDocumentLabel,
  pdfQuoteTotals,
  renderTotalsBreakdown,
  renderLogoWatermark,
} from './shared';

const SIDEBAR_WIDTH = 22;

/**
 * "Moderno" template — vertical colored sidebar on the left carrying the company data.
 * Content area on the right. Total in a highlighted accent block.
 */
export async function render(doc: jsPDF, data: PDFQuoteData): Promise<void> {
  const pageWidth = doc.internal.pageSize.getWidth();

  // Marca-d'agua da logo: PRIMEIRA instrucao de desenho da pagina.
  // jsPDF pinta na ordem das chamadas e nao tem z-index, entao desenhada
  // depois ela cobriria a tabela e o total em vez de ficar atras deles.
  await renderLogoWatermark(doc, data);
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 8;
  const contentX = SIDEBAR_WIDTH + margin;
  const contentWidth = pageWidth - contentX - margin;
  const { primaryColor, grayLight, grayText, darkText, white } =
    buildPalette(data.brandColorRGB);

  const logoBase64 = await loadLogo(data.company?.logoUrl);

  // === HEADER (content area) ===
  let yPos = margin + 6;
  doc.setTextColor(...darkText);
  doc.setFont('helvetica', 'bold');
  // A coluna de conteudo e o limite: a esquerda ha a barra lateral colorida.
  const tituloCabecalho = fitHeaderTitle(doc, resolveDocumentTitle(data), contentWidth, 24);
  doc.setFontSize(tituloCabecalho.size);
  doc.text(tituloCabecalho.text, contentX, yPos);
  // Subtitulo: 0 de avanco quando nao ha (o PDF do historico nao muda).
  yPos += renderDocumentSubtitle(doc, data, {
    x: contentX, y: yPos, maxWidth: contentWidth, color: grayText,
  });

  yPos += 4;
  doc.setDrawColor(...primaryColor);
  doc.setLineWidth(1.2);
  doc.line(contentX, yPos, contentX + 40, yPos);

  yPos += 8;
  doc.setFontSize(9);
  doc.setTextColor(...grayText);
  doc.setFont('helvetica', 'normal');
  doc.text(`Emissão: ${formatQuoteDate(data.quoteDate)}`, contentX, yPos);
  if (data.validityDays && data.quoteDate) {
    doc.text(
      `Válido até: ${calcValidityDate(data.quoteDate, data.validityDays)}`,
      pageWidth - margin,
      yPos,
      { align: 'right' },
    );
  }
  if (data.reference) {
    yPos += 5;
    doc.setFontSize(9);
    doc.setTextColor(...grayText);
    doc.text(`Referência: ${data.reference}`, contentX, yPos);
  }
  yPos += 8;

  // === CLIENT INFO ===
  doc.setFillColor(245, 246, 248);
  doc.roundedRect(contentX, yPos, contentWidth, 22, 2, 2, 'F');
  doc.setFontSize(8);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...primaryColor);
  doc.text('CLIENTE', contentX + 4, yPos + 6);

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  doc.setTextColor(...darkText);
  doc.text(data.client.name || 'Não informado', contentX + 4, yPos + 12);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  doc.setTextColor(...grayText);
  const info: string[] = [];
  if (data.client.phone) info.push(data.client.phone);
  if (data.client.document) info.push(clientDocumentLabel(data.client.document));
  if (data.client.address) info.push(data.client.address);
  if (info.length) {
    const line = doc.splitTextToSize(info.join(' · '), contentWidth - 8)[0];
    doc.text(line, contentX + 4, yPos + 18);
  }
  yPos += 22 + 10;

  // === ITEMS TABLE ===
  const { hasAnyDiscount, headers, body } = buildItemRows(data.items);
  const columnStyles = hasAnyDiscount
    ? {
        0: { cellWidth: 'auto' as const, halign: 'left' as const },
        1: { cellWidth: 14, halign: 'center' as const },
        2: { cellWidth: 12, halign: 'center' as const },
        3: { cellWidth: 26, halign: 'right' as const },
        4: { cellWidth: 20, halign: 'center' as const },
        5: { cellWidth: 28, halign: 'right' as const },
      }
    : {
        0: { cellWidth: 'auto' as const, halign: 'left' as const },
        1: { cellWidth: 16, halign: 'center' as const },
        2: { cellWidth: 12, halign: 'center' as const },
        3: { cellWidth: 30, halign: 'right' as const },
        4: { cellWidth: 30, halign: 'right' as const },
      };

  autoTable(doc, {
    startY: yPos,
    head: headers,
    body,
    theme: 'plain',
    headStyles: {
      fillColor: [255, 255, 255],
      textColor: darkText,
      fontStyle: 'bold',
      fontSize: 8,
      cellPadding: { top: 3, bottom: 3, left: 2, right: 2 },
      lineColor: darkText,
      lineWidth: { bottom: 0.7, top: 0, left: 0, right: 0 },
    },
    bodyStyles: {
      fontSize: 8,
      cellPadding: 3,
      lineColor: grayLight,
      lineWidth: { bottom: 0.2, top: 0, left: 0, right: 0 },
    },
    columnStyles,
    margin: { left: contentX, right: margin },
    // Redraw sidebar on every page
    didDrawPage: () => drawSidebar(doc, data, primaryColor, white, logoBase64),
  });

  const tableEndY = (doc as unknown as { lastAutoTable: { finalY: number } }).lastAutoTable.finalY;
  yPos = tableEndY + 6;

  // === TOTAL (highlight block) ===
  const totals = pdfQuoteTotals(data);
  const totalBoxWidth = 82;
  const totalBoxHeight = 18;
  const totalBoxX = pageWidth - margin - totalBoxWidth;
  yPos = renderTotalsBreakdown(doc, totals, yPos, {
    margin,
    pageWidth,
    grayText,
    width: totalBoxWidth,
  });
  doc.setFillColor(...primaryColor);
  doc.roundedRect(totalBoxX, yPos, totalBoxWidth, totalBoxHeight, 3, 3, 'F');

  doc.setTextColor(...white);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  doc.text('VALOR TOTAL', totalBoxX + 4, yPos + 7);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(15);
  doc.text(formatCurrency(totals.total), pageWidth - margin - 4, yPos + 13, { align: 'right' });

  yPos += totalBoxHeight + 10;

  // === OBSERVATIONS ===
  if (data.warranty || data.notes) {
    doc.setFontSize(9);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...primaryColor);
    doc.text('OBSERVAÇÕES', contentX, yPos);
    yPos += 5;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.setTextColor(...darkText);
    if (data.warranty) {
      const lines = doc.splitTextToSize(`Garantia: ${data.warranty}`, contentWidth);
      doc.text(lines, contentX, yPos);
      yPos += 4 * lines.length + 2;
    }
    if (data.notes) {
      const lines = doc.splitTextToSize(data.notes, contentWidth);
      doc.text(lines, contentX, yPos);
      yPos += 4 * lines.length + 4;
    }
  }

  // Formas aceitas + condições de pagamento. O retorno avança o `yPos`:
  // tudo abaixo (PIX, fotos, assinatura) é posicionado por coordenada.
  // === DIAGNÓSTICO TÉCNICO (Ordem de Serviço) ===
  // Só desenha quando a OS tem os campos preenchidos; devolve o yPos avançado
  // (jsPDF não tem layout — armadilha #13).
  yPos = renderServiceOrderSections(doc, data, yPos, {
    margin: contentX,
    pageWidth,
    pageHeight,
    grayText,
    darkText,
  });

  yPos = renderDiagnosisSection(doc, data, yPos, {
    margin: contentX,
    pageWidth,
    pageHeight,
    grayText,
    darkText,
  });

  // Campos extras do prestador: 0 de avanço quando não há (#L5).
  yPos = renderExtraFields(doc, data, yPos, {
    margin: contentX,
    pageWidth,
    pageHeight,
    grayText,
    darkText,
  });
  yPos = renderPaymentSection(doc, data, yPos, {
    margin: contentX,
    pageWidth,
    pageHeight,
    grayText,
    darkText,
  });

  yPos = await renderPixSection(doc, data, yPos, {
    primaryColor,
    grayText,
    darkText,
    margin: contentX,
    pageWidth,
    pageHeight,
  });

  if (data.termsConditions) {
    if (yPos > pageHeight - 60) {
      doc.addPage();
      yPos = margin + 6;
      drawSidebar(doc, data, primaryColor, white, logoBase64);
    }
    doc.setFontSize(9);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...primaryColor);
    doc.text('TERMOS E CONDIÇÕES', contentX, yPos);
    yPos += 5;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7);
    doc.setTextColor(...darkText);
    const lines = doc.splitTextToSize(data.termsConditions, contentWidth);
    doc.text(lines, contentX, yPos);
    yPos += 3.5 * lines.length + 4;
  }

  yPos = await renderPhotosSection(doc, data, yPos, {
    darkText,
    grayText,
    grayLight,
    margin: contentX,
    pageWidth,
    pageHeight,
  });

  await renderSignatureSection(doc, data, {
    darkText,
    grayText,
    margin: contentX,
    pageWidth,
    pageHeight,
    contentBottom: yPos,
  });

  renderFooter(doc, data, {
    primaryColor,
    grayText,
    margin: contentX,
    pageWidth,
    pageHeight,
  });

  // Ensure sidebar is drawn on all pages (in case autoTable didn't cover late pages)
  const totalPages = doc.getNumberOfPages();
  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);
    drawSidebar(doc, data, primaryColor, white, logoBase64);
  }
}

function drawSidebar(
  doc: jsPDF,
  data: PDFQuoteData,
  primaryColor: [number, number, number],
  white: [number, number, number],
  logoBase64: string | null,
): void {
  const pageHeight = doc.internal.pageSize.getHeight();
  doc.setFillColor(...primaryColor);
  doc.rect(0, 0, SIDEBAR_WIDTH, pageHeight, 'F');

  doc.setTextColor(...white);

  // Logo (small square) at top of sidebar
  let sy = 10;
  if (logoBase64) {
    try {
      const logoSize = SIDEBAR_WIDTH - 6;
      const format = getJsPdfImageFormat(logoBase64);
      doc.addImage(logoBase64, format, 3, sy, logoSize, logoSize);
      sy += logoSize + 6;
    } catch {
      /* ignore */
    }
  }

  // Company name (rotated vertical for the visual feel)
  if (data.company?.name) {
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.text(data.company.name, SIDEBAR_WIDTH / 2, sy, { align: 'center' });
    sy += 6;
  }

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(7);
  const infoLines: string[] = [];
  if (data.company?.cnpj) infoLines.push(`CNPJ ${data.company.cnpj}`);
  if (data.company?.phone) infoLines.push(data.company.phone);
  if (data.company?.email) infoLines.push(data.company.email);
  if (data.company?.address) infoLines.push(data.company.address);

  infoLines.forEach((line) => {
    const wrapped = doc.splitTextToSize(line, SIDEBAR_WIDTH - 4);
    wrapped.forEach((l: string) => {
      doc.text(l, SIDEBAR_WIDTH / 2, sy, { align: 'center' });
      sy += 3.5;
    });
    sy += 1;
  });
}
